import { useState, useEffect } from 'react';
import axios from '../../api/axios';
import './Events.css';
import { TextField } from '@mui/material';

import axiosInstance from '../../api/axios';

// Build a local Date from event.date + time ("H:MM", "HH:MM", "HH:MM:SS")
function toLocalDate(dateInput, timeStr) {
  if (!dateInput) return null;
  const base = new Date(dateInput);
  const y = base.getFullYear(),
    m = base.getMonth(),
    d = base.getDate();
  let hh = 0,
    mm = 0;
  if (typeof timeStr === 'string') {
    const m2 = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m2) {
      hh = Number(m2[1]);
      mm = Number(m2[2]);
    }
  }
  return new Date(y, m, d, hh, mm, 0, 0);
}

function formatEventDateRange(event) {
  const start = toLocalDate(event.date, event.startTime);
  const end = event.endTime ? toLocalDate(event.date, event.endTime) : null;

  const datePart = (start || new Date(event.date)).toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const timeFmt = { hour: 'numeric', minute: '2-digit', hour12: true };
  const s = start ? start.toLocaleTimeString([], timeFmt) : '';
  const e = end ? end.toLocaleTimeString([], timeFmt) : '';

  if (s && e) return `${datePart} • ${s} – ${e}`;
  if (s) return `${datePart} • ${s}`;
  return datePart; // legacy/all-day
}

const API_BASE_URL = axiosInstance.defaults.baseURL;

function Events() {
  // WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      const socket = new WebSocket(`ws://${window.location.hostname}:5001/ws`);

      socket.onopen = () => {
        // console.log('✅ WebSocket connected');
        socket.send('Hello server');
      };

      // socket.onmessage = (event) => {
      //   console.log('📩 Message from server:', event.data);
      // };

      socket.onclose = () => {
        console.log('❌ WebSocket disconnected');
      };
    }, 200); // delay connection

    return () => clearTimeout(timeout);
  }, []);
  const [search, setSearch] = useState('');
  const [events, setEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 8;
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [savedEvents, setSavedEvents] = useState(() => {
    const saved = localStorage.getItem('savedEvents');
    return saved ? JSON.parse(saved) : [];
  });
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    fetchEvents();
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events?future=true');
      const filtered = response.data.filter((event) => event.draft === false);
      const sorted = filtered.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setEvents(sorted);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const toggleSaveEvent = (eventId) => {
    let updated;
    if (savedEvents.includes(eventId)) {
      updated = savedEvents.filter((id) => id !== eventId);
    } else {
      updated = [...savedEvents, eventId];
    }
    setSavedEvents(updated);
    localStorage.setItem('savedEvents', JSON.stringify(updated));
  };

  const toggleReadMore = (id) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isUpcomingEvent = (e) => {
    const now = new Date();
    const start = toLocalDate(e.date, e.startTime || '00:00');
    const end = e.endTime ? toLocalDate(e.date, e.endTime) : start;
    const oneMonthFromNow = new Date(now);
    oneMonthFromNow.setMonth(now.getMonth() + 1);
    return end >= now && start <= oneMonthFromNow;
  };

  // Filter events based on search term
  const filteredEvents = events
    .filter((event) => {
      const matchesSearch =
        event.title.toLowerCase().includes(search.toLowerCase()) ||
        event.location.toLowerCase().includes(search.toLowerCase()) ||
        event.description.toLowerCase().includes(search.toLowerCase());

      const isSaved = !showSavedOnly || savedEvents.includes(event._id);
      const now = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(now.getMonth() + 1);
      const start = toLocalDate(event.date, event.startTime || '00:00');
      const end = event.endTime
        ? toLocalDate(event.date, event.endTime)
        : start;
      const isUpcoming =
        !showUpcomingOnly || (end >= now && start <= oneMonthFromNow);

      return matchesSearch && isSaved && isUpcoming;
    })
    .sort((a, b) => {
      if (showUpcomingOnly) {
        const aStart = toLocalDate(a.date, a.startTime || '00:00');
        const bStart = toLocalDate(b.date, b.startTime || '00:00');
        return aStart - bStart; // nearest first
      }
      if (sortBy === 'recent') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      if (sortBy === 'popular') {
        return (b.views || 0) - (a.views || 0);
      }
      return 0;
    });


  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(
    indexOfFirstEvent,
    indexOfLastEvent,
  );

  return (
    <div className="events-container">
      {/* Top Card */}
      <div className="events-top-card">
        <div className="events-search-field">
          <TextField
            variant="outlined"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="events-search-field"
            size="small"
          />
        </div>

        <div className="events-filters">
          <select
            className="alumni-events-select"
            onChange={(e) => setSortBy(e.target.value)}
            value={sortBy}
            style={{
              padding: '0.4rem 0.8rem',
              borderRadius: '4px',
              // marginRight: '1rem',
            }}
          >
            <option value="recent">Sort by Recent</option>
            <option value="popular">Sort by Popularity</option>
          </select>
          <button
            className={`show-saved-toggle ${showUpcomingOnly ? 'active' : ''}`}
            onClick={() => setShowUpcomingOnly((prev) => !prev)}
          >
            📅 {showUpcomingOnly ? 'Show All Events' : 'Show Upcoming Events'}
          </button>

          <button
            className={`show-saved-toggle ${showSavedOnly ? 'active' : ''}`}
            onClick={() => setShowSavedOnly((prev) => !prev)}
          >
            🔖 {showSavedOnly ? 'Show All Events' : 'Show Saved Events'}
          </button>
        </div>
      </div>

      <div className="alumni-events-grid">
        {filteredEvents.length > 0 ? (
          currentEvents.map((event) => (
            <div
              className="alumni-events-card"
              key={event._id}
              onClick={() => {
                axios.put(`/api/events/${event._id}/view`);
              }}
            >
              {isUpcomingEvent(event) && (
                <span className="new-badge">Upcoming Event</span>
              )}

              <img
                src={
                  event.image?.filePath
                    ? `${API_BASE_URL}/${event.image.filePath}`
                    : '/logo.png'
                }
                alt={event.title}
                className="events-image"
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewImage(`${API_BASE_URL}/${event.image?.filePath}`);
                }}
                style={{ cursor: 'pointer' }}
              />

              <h3 className="alumni-events-card-title">{event.title}</h3>

              <p style={{ fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>
                📅 {formatEventDateRange(event)}
              </p>

              <p
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  fontWeight: 600,
                  marginTop: '0.25rem',
                }}
              >
                📍 {event.location}
              </p>

              <p
                className={`alumni-events-card-description ${
                  !expandedDescriptions[event._id] ? 'truncate-description' : ''
                }`}
              >
                {event.description}
              </p>

              {event.description.split(' ').length > 10 && (
                <div
                  className="read-more-toggle"
                  onClick={() => toggleReadMore(event._id)}
                  style={{
                    color: '#007bff',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  {expandedDescriptions[event._id] ? 'Read less' : 'Read more'}
                </div>
              )}

              <p
                style={{
                  fontSize: '0.8rem',
                  color: '#888',
                  marginBottom: '0.5rem',
                }}
              >
                👁 {event.views || 0} views
              </p>

              <button
                className={`save-events-button ${
                  savedEvents.includes(event._id) ? 'saved' : ''
                }`}
                onClick={() => toggleSaveEvent(event._id)}
              >
                {savedEvents.includes(event._id) ? 'Unsave' : 'Save Event'}
              </button>
            </div>
          ))
        ) : (
          <div className="no-results-message">
            No events found for "<strong>{search}</strong>".
            <br />
            Try a different keyword or{' '}
            <span onClick={() => setSearch('')} className="show-all-link">
              view all events
            </span>
            .
          </div>
        )}
      </div>

      {filteredEvents.length > eventsPerPage && (
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          <span>
            Page {currentPage} of{' '}
            {Math.ceil(filteredEvents.length / eventsPerPage)}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) =>
                prev < Math.ceil(filteredEvents.length / eventsPerPage)
                  ? prev + 1
                  : prev,
              )
            }
            disabled={
              currentPage === Math.ceil(filteredEvents.length / eventsPerPage)
            }
          >
            Next
          </button>
        </div>
      )}

      {previewImage && (
        <div className="image-modal" onClick={() => setPreviewImage(null)}>
          <div
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={previewImage} alt="Preview" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
